/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DUAL Due Date — the core of Tendly
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * A rule "oil change every 12 months or 10,000 km" has two axes:
 *
 *   date axis  → lastDoneAt + intervalMonths
 *   usage axis → lastDoneUsage + intervalUsage, projected to a date using
 *                usage rate derived from reading history (km/day)
 *
 * Effective due date = whichever comes FIRST. If the last reading already
 * exceeds threshold, it is overdue NOW. Everything is pure and deterministic
 * (injectable `now`) so the projection is unit-testable.
 */

export const DUE_STATUS = ["overdue", "due_soon", "ok", "unknown"] as const;
export type DueStatus = (typeof DUE_STATUS)[number];

const MS_PER_DAY = 86_400_000;

/** Maximum historical window used to estimate usage rate. */
const USAGE_RATE_WINDOW_DAYS = 180;

/** Usage reading (km or hours) at a point in time. */
export interface UsagePoint {
  /** Absolute odometer/hour meter value. */
  value: number;
  /** ISO date of the reading. */
  recordedAt: string;
}

export interface DualDueInput {
  /** Every N months (null = no date axis). */
  intervalMonths: number | null;
  /** Every N km/hours (null = no usage axis). */
  intervalUsage: number | null;
  /** Last performed date (ISO). Without this the date axis produces no due date. */
  lastDoneAt: string | null;
  /** Reading when last performed. Without this the usage axis produces no threshold. */
  lastDoneUsage: number | null;
  /** Lead days to consider "due soon". */
  leadDays: number;
  /** Asset reading history (any order). */
  readings: UsagePoint[];
  /** Reference timestamp (default: now). */
  now?: Date;
}

export interface DualDueResult {
  /** Date axis due date (null if not applicable). */
  dueDate: Date | null;
  /** Usage axis threshold: reading at which it becomes due (null if not applicable). */
  dueUsage: number | null;
  /** Last known reading of the asset. */
  currentUsage: number | null;
  /** Estimated usage rate (units/day); null if not estimable. */
  usageRatePerDay: number | null;
  /** Projected date when usage threshold will be reached. */
  projectedUsageDate: Date | null;
  /** min(dueDate, projectedUsageDate) — whichever comes first. */
  effectiveDueDate: Date | null;
  /** Which axis determines the effective due date. */
  dueBy: "date" | "usage" | null;
  status: DueStatus;
  /** Days until effective due date (negative if overdue). */
  daysUntilDue: number | null;
  /** Remaining usage units until threshold (negative if exceeded). */
  usageUntilDue: number | null;
  /** Progress 0..1+ of the most advanced axis (for progress bars). */
  progress: number | null;
}

/** Adds calendar months clamping to month end (Jan 31 + 1 month → Feb 28/29). */
export function addMonthsClamped(date: Date, months: number): Date {
  const result = new Date(date);
  const targetDay = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const daysInTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(targetDay, daysInTargetMonth));
  return result;
}

function sortedValidReadings(readings: UsagePoint[]): UsagePoint[] {
  return readings
    .filter((r) => Number.isFinite(r.value) && !Number.isNaN(Date.parse(r.recordedAt)))
    .toSorted((a, b) => Date.parse(a.recordedAt) - Date.parse(b.recordedAt));
}

/**
 * Estimates usage rate (units/day) from history using a SIMPLE LINEAR
 * REGRESSION (least squares, NO AI) over recent readings: the slope
 * of `value` vs elapsed days is the average rate best explaining ALL
 * readings in the window, not just the two endpoints (an outlier reading
 * does not derail the projection).
 *
 * - Only readings within {@link USAGE_RATE_WINDOW_DAYS} days of `now` are
 *   considered; if fewer than two remain, it falls back to the two most recent
 *   (there is always something to project as long as ≥2 readings exist).
 * - With exactly two points, regression matches the line between them
 *   (two-point estimation).
 * - Returns null if there are not ≥2 readings separated in time, if all share
 *   the same instant (zero time variance), or if slope is not positive
 *   (stagnant or decreasing readings → cannot anticipate).
 */
export function estimateUsageRatePerDay(
  readings: UsagePoint[],
  now: Date = new Date(),
): number | null {
  const sorted = sortedValidReadings(readings);
  const REQUIRED_POINTS = 2;
  if (sorted.length < REQUIRED_POINTS) {
    return null;
  }

  const windowStart = now.getTime() - USAGE_RATE_WINDOW_DAYS * MS_PER_DAY;
  const inWindow = sorted.filter((r) => Date.parse(r.recordedAt) >= windowStart);
  // Entire window is old: use the two most recent readings available.
  const windowed = inWindow.length >= REQUIRED_POINTS ? inWindow : sorted.slice(-REQUIRED_POINTS);

  const origin = windowed[0];
  if (!origin) {
    return null;
  }
  // Least squares of value (y) vs days since first reading (x).
  const originMs = Date.parse(origin.recordedAt);
  const points = windowed.map((r) => ({
    x: (Date.parse(r.recordedAt) - originMs) / MS_PER_DAY,
    y: r.value,
  }));
  const n = points.length;
  const sumX = points.reduce((acc, p) => acc + p.x, 0);
  const sumY = points.reduce((acc, p) => acc + p.y, 0);
  const sumXX = points.reduce((acc, p) => acc + p.x * p.x, 0);
  const sumXY = points.reduce((acc, p) => acc + p.x * p.y, 0);

  const denominator = n * sumXX - sumX * sumX;
  if (denominator <= 0) {
    // All readings at same timestamp → no time variance.
    return null;
  }
  const slope = (n * sumXY - sumX * sumY) / denominator;
  if (slope <= 0) {
    return null;
  }
  return slope;
}

function resolveStatus(effectiveDueDate: Date | null, leadDays: number, now: Date): DueStatus {
  if (!effectiveDueDate) {
    return "unknown";
  }
  const msUntil = effectiveDueDate.getTime() - now.getTime();
  if (msUntil < 0) {
    return "overdue";
  }
  if (msUntil <= leadDays * MS_PER_DAY) {
    return "due_soon";
  }
  return "ok";
}

/**
 * Calculates effective due date of a dual rule.
 *
 * Rules:
 * - Date axis: needs `intervalMonths` + `lastDoneAt`.
 * - Usage axis: needs `intervalUsage` + `lastDoneUsage`. If last reading
 *   already reaches threshold → overdue NOW. Otherwise, date is projected using
 *   the historical estimated rate (if estimable).
 * - `effectiveDueDate` = earliest available date.
 * - Without any resolvable axis → `status: "unknown"` (UI requests input).
 * - Usage axis without possible projection (no readings/rate) but without exceeding
 *   threshold: if there is no date axis either, status is "ok" until exceeded
 *   (no way to anticipate further).
 */
// Function with many branches inherent to dual due logic (date axis +
// usage axis, with their "unknown"/"ok"/projection cases). Refactoring it
// risks altering behavior; complexity warning is suppressed without touching logic.
// oxlint-disable-next-line complexity
export function computeDualDue(input: DualDueInput): DualDueResult {
  const now = input.now ?? new Date();
  const sorted = sortedValidReadings(input.readings);
  const lastReading = sorted.at(-1) ?? null;
  const currentUsage = lastReading ? lastReading.value : null;

  // ── Date axis ──────────────────────────────────────────────────────────────
  let dueDate: Date | null = null;
  if (input.intervalMonths !== null && input.intervalMonths > 0 && input.lastDoneAt) {
    const baseline = new Date(input.lastDoneAt);
    if (!Number.isNaN(baseline.getTime())) {
      dueDate = addMonthsClamped(baseline, input.intervalMonths);
    }
  }

  // ── Usage axis ─────────────────────────────────────────────────────────────
  let dueUsage: number | null = null;
  if (input.intervalUsage !== null && input.intervalUsage > 0 && input.lastDoneUsage !== null) {
    dueUsage = input.lastDoneUsage + input.intervalUsage;
  }

  const usageRatePerDay = estimateUsageRatePerDay(sorted, now);
  let projectedUsageDate: Date | null = null;
  if (dueUsage !== null && currentUsage !== null) {
    if (currentUsage >= dueUsage && lastReading) {
      // Threshold already reached: overdue since date of last reading.
      projectedUsageDate = new Date(Date.parse(lastReading.recordedAt));
      if (projectedUsageDate.getTime() > now.getTime()) {
        projectedUsageDate = now;
      }
    } else if (usageRatePerDay !== null) {
      const daysToThreshold = (dueUsage - currentUsage) / usageRatePerDay;
      projectedUsageDate = new Date(now.getTime() + daysToThreshold * MS_PER_DAY);
    }
  }

  // ── Effective due date: whichever comes first ────────────────────────────
  let effectiveDueDate: Date | null = null;
  let dueBy: "date" | "usage" | null = null;
  if (dueDate && projectedUsageDate) {
    if (projectedUsageDate.getTime() <= dueDate.getTime()) {
      effectiveDueDate = projectedUsageDate;
      dueBy = "usage";
    } else {
      effectiveDueDate = dueDate;
      dueBy = "date";
    }
  } else if (dueDate) {
    effectiveDueDate = dueDate;
    dueBy = "date";
  } else if (projectedUsageDate) {
    effectiveDueDate = projectedUsageDate;
    dueBy = "usage";
  }

  let status = resolveStatus(effectiveDueDate, input.leadDays, now);
  const usageUntilDue = dueUsage !== null && currentUsage !== null ? dueUsage - currentUsage : null;
  if (status === "unknown" && usageUntilDue !== null) {
    // Active usage axis without projection (no rate): not overdue and cannot
    // anticipate further → "ok" showing remaining usage.
    status = usageUntilDue > 0 ? "ok" : "overdue";
  }

  // ── Progress (most advanced axis governs) ─────────────────────────────────
  let progress: number | null = null;
  if (dueDate && input.lastDoneAt) {
    const start = Date.parse(input.lastDoneAt);
    const span = dueDate.getTime() - start;
    if (span > 0) {
      progress = (now.getTime() - start) / span;
    }
  }
  if (
    dueUsage !== null &&
    input.lastDoneUsage !== null &&
    currentUsage !== null &&
    input.intervalUsage
  ) {
    const usageProgress = (currentUsage - input.lastDoneUsage) / input.intervalUsage;
    progress = progress === null ? usageProgress : Math.max(progress, usageProgress);
  }
  if (progress !== null) {
    progress = Math.max(0, progress);
  }

  return {
    dueDate,
    dueUsage,
    currentUsage,
    usageRatePerDay,
    projectedUsageDate,
    effectiveDueDate,
    dueBy,
    status,
    daysUntilDue:
      effectiveDueDate === null
        ? null
        : Math.ceil((effectiveDueDate.getTime() - now.getTime()) / MS_PER_DAY),
    usageUntilDue,
    progress,
  };
}

// ─── Warranties ───────────────────────────────────────────────────────────────

export interface WarrantyDueResult {
  expiresAt: Date;
  status: Exclude<DueStatus, "unknown">;
  daysUntilExpiry: number;
}

/** Default lead days to warn about an expiring warranty. */
export const WARRANTY_LEAD_DAYS = 30;

export function computeWarrantyDue(
  expiresAtISO: string,
  leadDays: number = WARRANTY_LEAD_DAYS,
  now: Date = new Date(),
): WarrantyDueResult {
  const expiresAt = new Date(expiresAtISO);
  const msUntil = expiresAt.getTime() - now.getTime();
  let status: WarrantyDueResult["status"] = "ok";
  if (msUntil < 0) {
    status = "overdue";
  } else if (msUntil <= leadDays * MS_PER_DAY) {
    status = "due_soon";
  }
  return {
    expiresAt,
    status,
    daysUntilExpiry: Math.ceil(msUntil / MS_PER_DAY),
  };
}

/** Warranty expiry = purchase + duration in months (materialized in DB). */
export function computeWarrantyExpiry(purchaseDateISO: string, durationMonths: number): Date {
  return addMonthsClamped(new Date(purchaseDateISO), durationMonths);
}
